import {LineItemModel} from '../../../../domain/models/checkout/LineItem.model';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {trimToMaxLength} from '../../../../domain/utils/trimToMaxLength';
import {getPaymentTypeLabel} from './LineItem.utils';
import {MAX_DESCRIPTION_LINE_LENGTH, MAX_MODIFIER_LENGTH, SPECS} from '../../../../common/constants';
import {ComWixEcommerceCatalogSpiApiV1PaymentOptionType} from '../../../../gql/graphql';
import {Experiments} from '@wix/yoshi-flow-editor';
import {DescriptionLineModel} from '../../../../domain/models/checkout/DescriptionLine.model';
import {ModifierModel} from '../../../../domain/models/checkout/Modifier.model';

const getQuantityDescriptionLine = (
  lineItem: LineItemModel,
  localeKeys: ILocaleKeys,
  experiments: Experiments
): string[] => {
  const hideQuantity =
    lineItem.renderingConfig?.hideQuantity ||
    (experiments.enabled(SPECS.AddLayoutConfigToLineItemsAutoGql) && lineItem.fixedQuantity);

  if (!hideQuantity) {
    return [`${localeKeys.checkout.order_summary.items.quantity()}: ${lineItem.quantity}`];
  }
  return [];
};

const getPaymentDescriptionLine = (lineItem: LineItemModel, localeKeys: ILocaleKeys): string[] => {
  if (lineItem.paymentOption !== ComWixEcommerceCatalogSpiApiV1PaymentOptionType.FULL_PAYMENT_ONLINE) {
    return [`${trimToMaxLength(getPaymentTypeLabel(lineItem, localeKeys), MAX_DESCRIPTION_LINE_LENGTH)}`];
  }
  return [];
};

const getSKUDescriptionLine = (lineItem: LineItemModel, localeKeys: ILocaleKeys): string[] => {
  if (lineItem.sku) {
    return [`${localeKeys.checkout.order_summary.items.sku()}: ${lineItem.sku}`];
  }
  return [];
};

export const mapLineItemToDetails = (
  lineItem: LineItemModel,
  experiments: Experiments,
  localeKeys: ILocaleKeys
): {
  descriptionLines?: string[];
  groups?: {descriptionLines: string[]; groupName?: string}[];
} => {
  const options: string[] = [
    ...getPaymentDescriptionLine(lineItem, localeKeys),
    ...getQuantityDescriptionLine(lineItem, localeKeys, experiments),
    ...getSKUDescriptionLine(lineItem, localeKeys),
    ...lineItem.descriptionLines.map(toDescriptionLineString),
  ];

  const modifierGroups = lineItem.modifierGroups;
  const hasOnlyOneSimpleGroup =
    modifierGroups?.length === 1 && !modifierGroups[0].name && modifierGroups[0].modifiers.length === 1;

  let groups;
  if (hasOnlyOneSimpleGroup && options.length === 0) {
    options.push(toModifierString(lineItem.modifierGroups[0].modifiers[0], localeKeys));
  } else {
    groups = lineItem.modifierGroups.map((group) => {
      return {
        groupName: group.name,
        descriptionLines: group.modifiers.map((modifier) => toModifierString(modifier, localeKeys)),
      };
    });
  }

  return {descriptionLines: options, groups};
};

const toDescriptionLineString = (descriptionLine: DescriptionLineModel): string => {
  if (!descriptionLine.title) {
    return trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH);
  }
  return `${descriptionLine.title}: ${trimToMaxLength(descriptionLine.value, MAX_DESCRIPTION_LINE_LENGTH)}`;
};

const toModifierString = (modifier: ModifierModel, localeKeys: ILocaleKeys): string => {
  let modifierName;
  if (!modifier.details) {
    modifierName = trimToMaxLength(modifier.label, MAX_MODIFIER_LENGTH);
  } else {
    modifierName = trimToMaxLength(`${modifier.label}: ${modifier.details}`, MAX_MODIFIER_LENGTH);
  }

  const modifierDetails = () => {
    if (modifier.quantity && modifier.price) {
      return 'quantityAndPrice';
    }
    if (modifier.quantity) {
      return 'quantity';
    }
    if (modifier.price) {
      return 'price';
    }
    return 'other';
  };

  return localeKeys.checkout.order_summary.items.modifierDetails({
    modifierDetails: modifierDetails(),
    quantity: modifier.quantity || undefined,
    price: modifier.price ? modifier.price.formattedAmount : undefined,
    modifierName,
  });
};
